import React, { useContext, useState } from "react";
import { useUser } from "../contexts/UserContext";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Popover,
  Paper,
  Stack,
} from "@mui/material";
import { ColorModeContext } from "../theme";
import { useNavigate, useLocation } from "react-router-dom";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutButton from "./LogoutButton";
import WebSocketStatusChip from "./WebSocketStatusChip";
import logo from "../assets/logo-menu.webp";
import { publicTopBarSx, PM_COLORS } from "../constants/publicMarketingTheme";

const PUBLIC_NAV_ITEMS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Features", path: "/features" },
  { label: "Integrations", path: "/integrations" },
  { label: "Pricing", path: "/pricing" },
  { label: "Contact", path: "/contact" },
  { label: "Blog", path: "/blog" },
];

const TopBar = () => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useUser();
  const hideSessionChrome =
    pathname === "/" || pathname === "/login";
  /** Landing/auth entry: always show marketing + auth CTAs in the bar. */
  const isGuestNavRoute =
    pathname === "/" ||
    pathname === "/login" ||
    pathname === "/signup" ||
    pathname === "/blog" ||
    pathname.startsWith("/blog/");
  const showPublicNav = !user || isGuestNavRoute;
  /** Home + login always use Sign up / Login; Dashboard only when signed in on /signup. */
  const showGuestAuthCtAs =
    !user || pathname === "/" || pathname === "/login";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  const themeToggleButton = (
    <IconButton
      data-onboarding-theme-toggle
      onClick={colorMode.toggleColorMode}
      color="inherit"
      aria-label={
        theme.palette.mode === "dark"
          ? "Switch to light mode"
          : "Switch to dark mode"
      }
    >
      {theme.palette.mode === "dark" ? (
        <Brightness7Icon />
      ) : (
        <Brightness4Icon />
      )}
    </IconButton>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        ...publicTopBarSx,
        top: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.drawer + 2,
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: !user ? "pointer" : "default" }}
          onClick={() => {
            if (!user) navigate("/");
          }}
        >
          <img
            src={logo}
            alt="Jim's Integrations Logo"
            width={48}
            height={48}
            style={{ height: 48, width: 48, marginRight: 16 }}
          />
          <Typography
            component="span"
            variant="h4"
            fontWeight={700}
            sx={{
              color: PM_COLORS.brandGold,
              fontFamily: "Playfair Display, serif",
            }}
          >
            JD Integrations
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {showPublicNav && !isMobile && (
            <>
              {PUBLIC_NAV_ITEMS.map(({ label, path }) => (
                <Button
                  key={path}
                  color="inherit"
                  onClick={() => navigate(path)}
                  sx={
                    pathname === path
                      ? { color: PM_COLORS.teal, fontWeight: 700 }
                      : undefined
                  }
                >
                  {label}
                </Button>
              ))}
            </>
          )}
          {user && !hideSessionChrome ? (
            <Box display="flex" alignItems="center" gap={0.5}>
              {!isMobile && !user.isPlatformAdmin ? <WebSocketStatusChip /> : null}
              {themeToggleButton}
              <LogoutButton />
            </Box>
          ) : (
            themeToggleButton
          )}
          {showPublicNav &&
            (isMobile ? (
              <>
                <IconButton
                  color="inherit"
                  onClick={(event) => setMobileMenuAnchor(event.currentTarget)}
                  aria-label="open auth menu"
                >
                  <MenuIcon />
                </IconButton>
                <Popover
                  open={Boolean(mobileMenuAnchor)}
                  anchorEl={mobileMenuAnchor}
                  onClose={() => setMobileMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <Paper sx={{ p: 1.5, minWidth: 180 }}>
                    <Stack spacing={1}>
                      {PUBLIC_NAV_ITEMS.map(({ label, path }) => (
                        <Button
                          key={path}
                          fullWidth
                          color="inherit"
                          variant="text"
                          onClick={() => {
                            navigate(path);
                            setMobileMenuAnchor(null);
                          }}
                          sx={
                            pathname === path
                              ? { color: PM_COLORS.teal, fontWeight: 700 }
                              : undefined
                          }
                        >
                          {label}
                        </Button>
                      ))}
                      {!showGuestAuthCtAs && user && pathname === "/signup" ? (
                        <Button
                          fullWidth
                          color="secondary"
                          variant="contained"
                          onClick={() => {
                            navigate("/dashboard");
                            setMobileMenuAnchor(null);
                          }}
                        >
                          Dashboard
                        </Button>
                      ) : (
                        <>
                          <Button
                            fullWidth
                            color="secondary"
                            variant="outlined"
                            onClick={() => {
                              navigate("/signup");
                              setMobileMenuAnchor(null);
                            }}
                          >
                            Sign Up
                          </Button>
                          <Button
                            fullWidth
                            color="secondary"
                            variant="contained"
                            onClick={() => {
                              navigate("/login");
                              setMobileMenuAnchor(null);
                            }}
                          >
                            Log In
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Paper>
                </Popover>
              </>
            ) : !showGuestAuthCtAs && user && pathname === "/signup" ? (
              <Button
                color="secondary"
                variant="contained"
                onClick={() => navigate("/dashboard")}
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => navigate("/signup")}
                >
                  Sign Up
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => navigate("/login")}
                >
                  Login
                </Button>
              </>
            ))}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
